import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import PropTypes from 'prop-types';
import IconButton from "../../../common/icon-button/icon-button";
import TextOverflowTooltip from "../../../common/TextOverflowTooltip";
import WidgetOptions from "../widget-options/widget-options";
import Search from "../../../common/form-elements/search/Search";
import NetworkToolsConfigForm from "./NetworkToolsConfigForm";
import ModalDefault from "../../../common/modal/modal-default";
import { REQUIRED_FIELD_MESSAGE } from "../../../../helpers/validation-helpers";
import { shutdownFeed } from '../widget-helpers';
import { getClassName } from "../../../../helpers/dom-helpers";
import { steveDataStorage } from "../../../../helpers/sdk-helpers";
import FormErrorMessage from "../../../common/form-elements/form-error-message/form-error-message";
import DashboardService from "../../../../services/dashboard-service";
import Loader from "../../../common/loader/Loader";
import { COMMANDS, IP_OR_HOSTNAME_REGEX, PING_PACKET_SIZE_OPTIONS, STRINGS_TO_REMOVE,
  SOURCE_PLACEHOLDER, TARGET_PLACEHOLDER } from './constants';
import "./network-tools.scss";

const INITIAL_VALUES = {
  title: "",
  pingEchoCount: "5",
  pingPacketSize: PING_PACKET_SIZE_OPTIONS[0],
  commandResults: [],
  source: { searchValue: '' },
  target: { searchValue: '' }
};

const NetworkTools = ({
  showWidgetInfo,
  onRemove,
  onClone,
  onUpdate,
  isEditAllowed,
  data
}) => {
  const dataFromStorage = useMemo(() => steveDataStorage(
    'networkTools',
    data.id,
    false
  ).get('commandData'), [data]);
  const sourceInitialValue = dataFromStorage?.source || INITIAL_VALUES.source;
  const targetInitialValue = dataFromStorage?.target || INITIAL_VALUES.target;
  const commandResultsInitialValue = dataFromStorage?.commandResults || INITIAL_VALUES.commandResults;
  const [configFormData, setConfigFormData] = useState({
    title: data.title || INITIAL_VALUES.title,
    pingEchoCount: data.config.pingEchoCount || INITIAL_VALUES.pingEchoCount,
    pingPacketSize: data.config.pingPacketSize || INITIAL_VALUES.pingPacketSize,
  });
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isSettingsModalShown, setIsSettingsModalShown] = useState(false);
  const [searchResultsSource, setSearchResultsSource] = useState([]);
  const [searchResultsTarget, setSearchResultsTarget] = useState([]);
  const [isTargetSelected, setIsTargetSelected] = useState(false);
  const [isSourceSelected, setIsSourceSelected] = useState(false);
  const [source, setSource] = useState(sourceInitialValue);
  const [target, setTarget] = useState(targetInitialValue);
  const [sourceFieldValue, setSourceFieldValue] = useState("");
  const [targetFieldValue, setTargetFieldValue] = useState("");
  const [commandResults, setCommandResults] = useState(commandResultsInitialValue);
  const [expandedDisplay, setExpandedDisplay] = useState(false);
  const [runningCommand, setRunningCommand] = useState(null);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const networkToolsFormRef = useRef();
  const componentIsMounted = useRef(false);
  const jobFeedRef = useRef();

  useEffect(() => {
    componentIsMounted.current = true;
    return () => {
      componentIsMounted.current = false;
      shutdownFeed(jobFeedRef.current);
    }
  }, []);

  const setValuesFromStorage = useCallback(async () => {
    if (dataFromStorage) {
        const sourceList = await getDevices(dataFromStorage.source.searchValue);
        setSearchResultsSource(sourceList);
        if (sourceList.length) {
          setIsSourceSelected(true);
        }
        const targetList = await getDevices(dataFromStorage.target.searchValue);
        setSearchResultsTarget(targetList);
        if (targetList.length) {
          setIsTargetSelected(true);
        }
      setCommandResults(dataFromStorage.commandResults);
    }
    setInitialDataLoaded(true);
  }, [dataFromStorage]);

  useEffect(() => {
    setValuesFromStorage();
  }, [setValuesFromStorage]);

  const toggleSettingsModal = (e) => {
    e.stopPropagation();
    setIsSettingsModalShown(value => !value);
  };

  const switchIconName = useMemo(
    () => (isTargetSelected && isSourceSelected && !runningCommand ? 'g-icon-switch-inputs' : ''),
    [isTargetSelected, isSourceSelected, runningCommand]
  );

  const pingBtnName = useMemo(() => runningCommand === COMMANDS.PING ? 'Stop' : 'Ping', [runningCommand]);

  const tracerouteBtnName = useMemo(() => runningCommand === COMMANDS.TRACEROUTE ? 'Stop' : 'Traceroute', [runningCommand]);

  const getDevices = async (userInput) => {
    let devices;
    try {
      devices = await DashboardService.loadDevices(userInput);
    } catch {
      return [];
    }

    if (!devices?.length) {
      return [];
    }

    const result = devices.map(device => {
      const name = device.getName();
      const hostname = device.getDetectedHostName();
      const ip = device.getConnectionInformation().ip;
      const serialNumber = device.getDetectedSerialNumber();

      return {
        searchValue: name.toLowerCase().includes(userInput.toLowerCase()) ? name : ip,
        name,
        ip,
        hostname,
        serialNumber
      }
    })

    return result;
  };

  const searchSource = async value => {
    const results = await getDevices(value);
    if (componentIsMounted.current) {
      setSourceFieldValue(value);
      setSearchResultsSource(results);
      const source = results.find(item => item.searchValue === value);
      setSource(source);
    }
  };

  const searchTarget = async value => {
    const results = await getDevices(value);
    if (componentIsMounted.current) {
      setTargetFieldValue(value);
      setSearchResultsTarget(results);
      const target = results.find(item => item.searchValue === value) || { searchValue: value };
      setTarget(target);
    }
  };

  const updateSource = (value) => {
    const source = searchResultsSource.find(item => item.searchValue === value);
    setSource(source);
  };

  const updateTarget = (value) => {
    const target = searchResultsTarget.find(item => item.searchValue === value) || { searchValue: value };
    setTarget(target);
  };

  const handleSwitchInputs = (form) => {
    form.setFieldValue("source", target.searchValue);
    form.setFieldValue("target", source.searchValue);
    setSearchResultsTarget(searchResultsSource);
    setSearchResultsSource(searchResultsTarget);
    setSource(target);
    setTarget(source);
  };

  const formatCommandResultText = (text) => {
    if (source?.hostname){
      text = text.replace(new RegExp(`${source.hostname}#`, 'g'), source.hostname);
    }
    if (source?.serialNumber){
      text = text.replace(new RegExp(`${source.serialNumber}#`, 'g'), source.serialNumber);
    }
    text = text.replace(new RegExp(STRINGS_TO_REMOVE, 'g'), "");

    return text.replace('\r\n', "")
  }

  const saveCommandDataToSteveStorage = (source, target, commandResults) => {
    steveDataStorage('networkTools', data.id, false).set({
      source,
      target,
      commandResults
    }, 'commandData');
  };

  const handleResponse = (response) => {
    const formattedResponse = response.map(item => formatCommandResultText(item));
    setCommandResults(formattedResponse);
    setRunningCommand(null);
    saveCommandDataToSteveStorage(source, target, formattedResponse);
    setIsLoadingData(false);
  }

  const handleError = (message) => {
    setCommandResults(message);
    setRunningCommand(null);
    setIsLoadingData(false);
  }

  const checkJobCompletion = (job, jobFeed) => {
    if (job && job.finishObj) {
      jobFeed.shutdown();
      if (job.finishObj.errorMsg) {
        handleResponse([job.finishObj.errorMsg])
      } else {
        handleResponse(job.finishObj.responses);
      }
    }
  };

  const addFeedListeners = (feed, jobId) => {
    feed
      .on("ChangeFeedInitialized", (e) => {
        checkJobCompletion(e[jobId], feed);
      })
      .on("EntityAdded", (e) => {
        checkJobCompletion(e, feed);
      })
      .on("EntityPropertiesUpdated", (e) => {
        checkJobCompletion(e, feed);
      });
  }

  const onClickCommand = async (commandType) => {
    setIsLoadingData(true);
    setRunningCommand(commandType);

    let targetForCommand = target.ip || target.searchValue;
    let command = '';
    if (
      commandType === COMMANDS.PING &&
      (configFormData.pingEchoCount !== INITIAL_VALUES.pingEchoCount ||
        configFormData.pingPacketSize.value !== INITIAL_VALUES.pingPacketSize.value)
    ) {
      command = `${commandType} ${targetForCommand} repeat ${configFormData.pingEchoCount} size ${configFormData.pingPacketSize.value}`;
    } else {
      command = `${commandType} ${targetForCommand}`;
    }

    try {
      const jobId = await DashboardService.launchJob(source.name, command);
      jobFeedRef.current = await DashboardService.getJobFeedForCommand(jobId);

      addFeedListeners(jobFeedRef.current, jobId);
    } catch (error) {
      handleError(['Something went wrong.'])
    }
  };

  const onClickStop = async () => {
    await shutdownFeed(jobFeedRef.current);

    setCommandResults(INITIAL_VALUES.commandResults);
    setRunningCommand(null);
    setIsLoadingData(false);
  };

  const toggleExpandDisplay = () => {
    setExpandedDisplay(value => !value);
  };

  const handleSave = (values) => {
    setConfigFormData(values);
    onUpdate({
      ...data,
      title: values.title,
      config: {
        ...data.config,
        pingEchoCount: values.pingEchoCount,
        pingPacketSize: values.pingPacketSize,
      },
    });
    setIsSettingsModalShown(false);
  };

  const validateSourceInput = (value) => {
    if (!value) {
      return REQUIRED_FIELD_MESSAGE;
    }
    if (!searchResultsSource.find((device) => value === device.searchValue)) {
      return "This must be from devices list";
    }
  };

  const validateTargetInput = (value) => {
    if (!value) {
      return REQUIRED_FIELD_MESSAGE;
    }
    if (!searchResultsTarget.find((device) => value === device.searchValue) && !IP_OR_HOSTNAME_REGEX.test(value)) {
      return "This must be valid hostname or IP address";
    }
  };

  useEffect(() => {
    // need to revalidate fields after async operations are finished
    if (networkToolsFormRef) {
      networkToolsFormRef.current.validateForm();
    }
    if (searchResultsSource.find((device) => sourceFieldValue === device.searchValue)) {
      setIsSourceSelected(true);
    }
    if (searchResultsTarget.find((device) => targetFieldValue === device.searchValue)) {
      setIsTargetSelected(true);
    }
  }, [searchResultsSource, searchResultsTarget, sourceFieldValue, targetFieldValue]);

  return (
    <div className="glu-dashboard-card glu-content">
      <div className="glu-content__header">
        {isEditAllowed && (
          <div className="glu-flex-shrink">
            <IconButton
              className="glu-icon__draggable cursor-move"
              icon="drag"
            />
          </div>
        )}
        <TextOverflowTooltip
          title={data.title}
          renderChildren={(ref) => (
            <div className="glu-flex-grow">
              <div className="glu-h3 glu-widget-title" ref={ref}>
                {data.title}
              </div>
            </div>
          )}
        />
        <div className="glu-flex-row">
          {isEditAllowed && (
            <WidgetOptions
              showWidgetInfo={showWidgetInfo(data)}
              openConfiguration={toggleSettingsModal}
              onRemove={onRemove(data)}
              onClone={onClone(data)}
            />
          )}
        </div>
      </div>
      <div className="glu-content__body">
        <div className="glu-content__section glu-content__separator glu-content__separator--inner glu-flex-col glu-network--wrapper">
          <Formik
            initialValues={{
              source: sourceInitialValue.searchValue,
              target: targetInitialValue.searchValue,
            }}
            innerRef={networkToolsFormRef}
          >
            {(props) => {
              return (
                <div
                  className={getClassName(`glu-network-form`, {
                    "glu-network-form--none": expandedDisplay,
                  })}
                >
                  {initialDataLoaded && (
                    <Form>
                      <div className="glu-modal__body">
                        <div className="glu-form-row">
                          <div className="glu-label">Enter Source</div>
                          <Field
                            as={Search}
                            name="source"
                            form={props}
                            className="glu-form-input-pad"
                            autoFocus
                            autoComplete="off"
                            placeholder={SOURCE_PLACEHOLDER}
                            validate={validateSourceInput}
                            invalid={props.errors.name && props.touched.name}
                            value={props.values.source}
                            onSearch={searchSource}
                            searchResults={searchResultsSource.map(
                              (item) => item.searchValue
                            )}
                            onSelectItem={setIsSourceSelected}
                            rightIconClass={switchIconName}
                            onClickRightIcon={() => handleSwitchInputs(props)}
                            updateParentValue={updateSource}
                            isDisabled={isLoadingData}
                          />
                          <ErrorMessage
                            name="source"
                            component={FormErrorMessage}
                          />
                        </div>

                        <div className="glu-form-row">
                          <div className="glu-label">
                            Enter hostname or IP address
                          </div>
                          <Field
                            as={Search}
                            name="target"
                            form={props}
                            className="glu-form-input-pad"
                            autoComplete="off"
                            placeholder={TARGET_PLACEHOLDER}
                            validate={validateTargetInput}
                            invalid={props.errors.name && props.touched.name}
                            value={props.values.target}
                            onSearch={searchTarget}
                            searchResults={searchResultsTarget.map(
                              (item) => item.searchValue
                            )}
                            onSelectItem={setIsTargetSelected}
                            updateParentValue={updateTarget}
                            isDisabled={isLoadingData}
                          />
                          <ErrorMessage
                            name="target"
                            component={FormErrorMessage}
                          />
                        </div>
                      </div>

                      <div className="glu-network-commands">
                        <div className="glu-flex-grow glu-flex-row">
                          <div className="glu-flex-grow text-right">
                            <button
                              type="button"
                              onClick={
                                runningCommand === COMMANDS.PING
                                  ? () => onClickStop()
                                  : () => onClickCommand(COMMANDS.PING)
                              }
                              className={getClassName(
                                `glu-btn`,
                                `glu-btn--primary`
                              )}
                              disabled={
                                runningCommand === COMMANDS.TRACEROUTE ||
                                Object.keys(props.errors).length > 0 ||
                                props.values.source === "" ||
                                props.values.target === "" || !source || !target
                              }
                            >
                              {pingBtnName}
                            </button>
                          </div>

                          <div className="glu-flex-shrink">
                            <button
                              type="button"
                              onClick={
                                runningCommand === COMMANDS.TRACEROUTE
                                  ? () => onClickStop()
                                  : () => onClickCommand(COMMANDS.TRACEROUTE)
                              }
                              className={getClassName(
                                `glu-btn`,
                                `glu-btn--primary`
                              )}
                              disabled={
                                runningCommand === COMMANDS.PING ||
                                Object.keys(props.errors).length > 0 ||
                                props.values.source === "" ||
                                props.values.target === "" || !source || !target
                              }
                            >
                              {tracerouteBtnName}
                            </button>
                          </div>
                        </div>
                      </div>
                    </Form>
                  )}
                </div>
              );
            }}
          </Formik>

          <div
            className={getClassName("glu-network-display--wrapper", {
              "glu-network-display--wrapper--off": isLoadingData,
            })}
          >
            {runningCommand ? (
              <div className="glu-network-display__spinner--wrapper">
                <Loader
                  customClass="size-small"
                  loadingText={runningCommand === COMMANDS.PING ? 'Pinging' : 'Tracing'}
                />
              </div>
            ) : (
              <>
                <div className="glu-network-display">
                  {commandResults.map((item, ind) => (
                    <div key={ind} className="glu-network-display__item">
                      {item}
                    </div>
                  ))}
                </div>
                <IconButton
                  onClick={toggleExpandDisplay}
                  icon={expandedDisplay ? "unfold-less" : "unfold-more"}
                  className={"glu-network-display__button"}
                />
              </>
            )}
          </div>
        </div>
      </div>

      {isSettingsModalShown && (
        <ModalDefault isOpen={isSettingsModalShown}>
          <div className="glu-modal-content">
            <div className="glu-modal__header">
              <div className="glu-modal__title glu-flex-grow">Network Tools</div>
              <div className="glu-flex-shrink">
                <button
                  aria-label="close"
                  type="button"
                  className="glu-btn-link g-icon g-icon-close"
                  onClick={toggleSettingsModal}
                />
              </div>
            </div>
            <NetworkToolsConfigForm
              initialValues={configFormData}
              onFormSubmit={handleSave}
              onCancel={toggleSettingsModal}
              pingPacketSizeOptions={PING_PACKET_SIZE_OPTIONS}
              pingPacketSizeDeafult={INITIAL_VALUES.pingPacketSize.value}
            />
          </div>
        </ModalDefault>
      )}
    </div>
  );
};

NetworkTools.propTypes = {
  showWidgetInfo: PropTypes.func,
  onRemove: PropTypes.func,
  onClone: PropTypes.func,
  onUpdate: PropTypes.func,
  isEditAllowed: PropTypes.bool,
  data: PropTypes.objectOf(PropTypes.any)
};

NetworkTools.defaultProps = {
  showWidgetInfo: undefined,
  onRemove: undefined,
  onClone: undefined,
  onUpdate: undefined,
  isEditAllowed: false
}

export default NetworkTools;
